import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TestimonialCard } from '@/components/TestimonialCard';
import { TestimonialForm } from '@/components/TestimonialForm';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { Testimonial, useTestimonials } from '@/hooks/useTestimonials';

export const AdminTestimonialsList = () => {
  const { testimonials, loading, addTestimonial, updateTestimonial, deleteTestimonial, toggleVisibility } = useTestimonials(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleAddTestimonial = async (data: Omit<Testimonial, 'id' | 'created_at' | 'updated_at'>) => {
    return addTestimonial(data);
  };

  const handleEditTestimonial = async (data: Omit<Testimonial, 'id' | 'created_at' | 'updated_at'>) => {
    if (!editingTestimonial) return { success: false };
    const result = await updateTestimonial(editingTestimonial.id, data);
    if (result.success) {
      setEditingTestimonial(null);
    }
    return result;
  };

  const handleDeleteTestimonial = async () => {
    if (!deletingId) return;
    const result = await deleteTestimonial(deletingId);
    if (result.success) {
      setDeletingId(null);
    }
  };

  const handleToggleVisibility = async (id: string, visible: boolean) => {
    await toggleVisibility(id, visible);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Chargement des avis...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">Gestion des avis</h3>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un avis
        </Button>
      </div>

      <div className="grid gap-4">
        {testimonials.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">Aucun avis trouvé</p>
            </CardContent>
          </Card>
        ) : (
          testimonials.map((testimonial) => (
            <Card key={testimonial.id} className="relative">
              <CardContent className="p-4">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <TestimonialCard {...testimonial} />
                  </div>
                  
                  <div className="flex flex-col gap-2 items-end">
                    <div className="flex gap-2">
                      <Badge variant={testimonial.visible ? "default" : "secondary"}>
                        {testimonial.visible ? "Visible" : "Masqué"}
                      </Badge>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleVisibility(testimonial.id, !testimonial.visible)}
                      >
                        {testimonial.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingTestimonial(testimonial)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeletingId(testimonial.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Form */}
      <TestimonialForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleAddTestimonial}
        title="Ajouter un nouvel avis"
      />

      {/* Edit Form */}
      <TestimonialForm
        isOpen={!!editingTestimonial}
        onClose={() => setEditingTestimonial(null)}
        onSubmit={handleEditTestimonial}
        testimonial={editingTestimonial || undefined}
        title="Modifier l'avis"
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingId} onOpenChange={() => setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cet avis ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTestimonial}>
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};